import { GAME_MODES, type BlindKingsGame } from '@blind-kings/game-core';
import { api } from 'aws-blocks';
import { useRef, useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import ProfileAvatar from '../../Components/ProfileAvatar';
import Timer from '../../Components/Timer';
import useApiAction from '../../hooks/use-api-action';
import {
  getDefaultSkinIdForTeam,
  getPlayerSkinDef,
  skinLabelStyle,
} from '../../player-colors';

export type GameHeaderProps = {
  blindKingsGame: BlindKingsGame;
  trailing?: ReactNode;
  emote?: ReactNode;
  firstBurst?: ReactNode;
  secondBurst?: ReactNode;
};

export default function GameHeader({
  blindKingsGame,
  trailing,
  emote,
  firstBurst,
  secondBurst,
}: GameHeaderProps) {
  const {
    userIsInGame,
    isGameOver,
    isUserTurn,
    isFirstPlayersTurn,
    turnsWithoutAction,
    maxTurnsWithoutAction,
    turnLength,
    turnFinishBy,
    gameMode,
    turnPlayer,
    firstPlayer,
    secondPlayer,
    firstPlayerWon,
    gameWinner,
  } = blindKingsGame;

  const { gameId } = useParams();
  const timeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const [timeLeft, setTimeLeft] = useState<number | null>(null);

  const [sendTimeOut] = useApiAction({
    action: async () => api.gameTimerFinished(String(gameId)),
    quietly: true,
  });

  const lastTurns = maxTurnsWithoutAction - turnsWithoutAction <= 5;

  const activeSeat = isGameOver
    ? firstPlayerWon
      ? firstPlayer
      : secondPlayer
    : isFirstPlayersTurn
      ? firstPlayer
      : secondPlayer;

  const activeLabel = useMemo(() => {
    if (gameMode === GAME_MODES.LOCAL_GAME) {
      return isFirstPlayersTurn ? 'Player 1' : 'Player 2';
    }
    return (
      turnPlayer?.displayName ||
      (isFirstPlayersTurn ? firstPlayer?.displayName : secondPlayer?.displayName) ||
      (isFirstPlayersTurn ? 'Player 1' : 'Player 2')
    );
  }, [
    gameMode,
    isFirstPlayersTurn,
    turnPlayer?.displayName,
    firstPlayer?.displayName,
    secondPlayer?.displayName,
  ]);

  const winnerLabel = useMemo(() => {
    if (gameMode === GAME_MODES.LOCAL_GAME) {
      return firstPlayerWon ? 'Player 1 Wins' : 'Player 2 Wins';
    }
    const winner =
      (typeof gameWinner === 'object' && gameWinner ? gameWinner : null) ??
      (firstPlayerWon ? firstPlayer : secondPlayer);
    const name =
      winner?.displayName ||
      (firstPlayerWon ? firstPlayer?.displayName : secondPlayer?.displayName) ||
      (firstPlayerWon ? 'Player 1' : 'Player 2');
    return `${name} Wins`;
  }, [
    gameMode,
    firstPlayerWon,
    gameWinner,
    firstPlayer?.displayName,
    secondPlayer?.displayName,
  ]);

  const bannerColor = useMemo(() => {
    const winnerSeat = isGameOver ? firstPlayerWon : isFirstPlayersTurn;
    const colorId = winnerSeat
      ? ((firstPlayer?.colorId as string | undefined) ?? getDefaultSkinIdForTeam(0))
      : ((secondPlayer?.colorId as string | undefined) ?? getDefaultSkinIdForTeam(1));
    return getPlayerSkinDef(colorId);
  }, [
    isGameOver,
    firstPlayerWon,
    isFirstPlayersTurn,
    firstPlayer?.colorId,
    secondPlayer?.colorId,
  ]);

  const bannerLabelCss = useMemo(() => skinLabelStyle(bannerColor), [bannerColor]);

  const handleTimerFinished = useCallback(() => {
    if (userIsInGame) void sendTimeOut();
    else {
      timeoutRef.current = setTimeout(() => {
        void sendTimeOut();
      }, 2000);
    }
  }, [userIsInGame, sendTimeOut]);

  useEffect(() => {
    if (isGameOver && timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, [isGameOver]);

  useEffect(() => {
    if (!turnFinishBy || isGameOver) {
      setTimeLeft(null);
      return;
    }
    const tick = () =>
      setTimeLeft(Math.max(0, Math.ceil((turnFinishBy - Date.now()) / 1000)));
    tick();
    const id = setInterval(tick, 500);
    return () => clearInterval(id);
  }, [turnFinishBy, isGameOver]);

  const yourTurn = !isGameOver && !!isUserTurn && userIsInGame;
  const urgent =
    yourTurn && timeLeft !== null && timeLeft > 0 && timeLeft <= 15;

  const stallLabel = (
    <span className={lastTurns ? 'font-bold text-bk-danger' : 'text-bk-muted'}>
      Stall {turnsWithoutAction}/{maxTurnsWithoutAction}
    </span>
  );

  const avatarId = (activeSeat as { avatarId?: string } | undefined)?.avatarId;

  const turnHint = isGameOver
    ? null
    : yourTurn
      ? urgent
        ? 'Hurry — your turn'
        : 'Your turn'
      : gameMode === GAME_MODES.LOCAL_GAME
        ? null
        : 'Opponent turn';

  return (
    <div
      className={[
        'relative z-30 shrink-0 overflow-visible border bg-bk-panel/80 px-2 py-1.5 backdrop-blur-sm sm:px-3 sm:py-2',
        yourTurn
          ? urgent
            ? 'animate-bk-turn-flash border-bk-danger-bright/80'
            : 'border-bk-gold shadow-[0_0_16px_color-mix(in_srgb,var(--color-bk-gold)_28%,transparent)]'
          : 'border-bk-gold-dim/40',
      ].join(' ')}
    >
      <div
        className={`relative flex min-h-8 w-full items-center justify-center gap-2 sm:min-h-10 ${
          trailing ? 'pr-16 sm:pr-24' : emote ? 'pr-12 sm:pr-16' : ''
        } pl-10 sm:pl-12`}
      >
        <div className="absolute inset-y-0 left-0 z-10 flex items-center">
          <span className="relative flex h-8 w-8 items-center justify-center">
            {firstBurst}
          </span>
        </div>
        {gameMode !== GAME_MODES.LOCAL_GAME ? (
          <ProfileAvatar avatarId={avatarId} size="sm" className="!h-8 !w-8" />
        ) : null}
        <div className="min-w-0 text-center">
          {turnHint ? (
            <div
              className={[
                'text-[10px] font-bold uppercase tracking-[0.2em] sm:text-xs',
                urgent
                  ? 'animate-bk-urgent text-bk-danger-bright'
                  : yourTurn
                    ? 'text-bk-gold-bright'
                    : 'text-bk-muted',
              ].join(' ')}
            >
              {turnHint}
            </div>
          ) : null}
          <span
            className={[
              'block truncate text-center text-base font-extrabold tracking-wide sm:text-lg md:text-2xl',
              yourTurn && !isGameOver ? 'animate-bk-pulse-soft' : '',
            ].join(' ')}
            style={bannerLabelCss}
          >
            {isGameOver ? winnerLabel : activeLabel}
          </span>
        </div>
        <div className="absolute inset-y-0 right-0 z-40 flex items-center overflow-visible">
          <span className="relative flex items-center justify-center overflow-visible">
            {secondBurst}
            {trailing ?? emote}
          </span>
        </div>
      </div>
      {!isGameOver && turnLength ? (
        <Timer
          endTime={turnFinishBy}
          length={turnLength}
          warnAtTimeLeft={30}
          dangerAtTimeLeft={15}
          onFinish={handleTimerFinished}
          compact
          leading={stallLabel}
        />
      ) : !isGameOver ? (
        <div className="mt-1 text-center text-xs sm:text-sm">{stallLabel}</div>
      ) : null}
    </div>
  );
}
